import { useEffect, useMemo } from 'react';
import {
  type Entitlement,
  useFeatureEntitlements,
  usePermissionEntitlements,
} from '@frontegg/nextjs';
import { UserSubscriptionType } from '../services/subscriptions/subscriptions.types';
import {
  FronteggFeatureKeys,
  type UserEntitlementsContextType,
} from './userEntitlementsContext.types';
import { getUserPlanType } from '../hooks/useUserSubscription/helpers/getUserPlanType';

// Authenticated provider - uses frontegg entitlements
const useAuthenticatedUserEntitlementsContext =
  (): UserEntitlementsContextType => {
    const { isEntitled: isPaid } = useFeatureEntitlements(
      FronteggFeatureKeys.LegacyIsPaid,
    );
    const { isEntitled: hasProPlan } = useFeatureEntitlements(
      FronteggFeatureKeys.ProPlan,
    );
    const { isEntitled: hasPremiumPlan } = useFeatureEntitlements(
      FronteggFeatureKeys.PremiumPlan,
    );
    const { isEntitled: isTrustedCustomer } = useFeatureEntitlements(
      FronteggFeatureKeys.TrustedCustomer,
    );

    // Custom hooks for patient-specific entitlements - always throw for providers
    const useAuthenticatedPatientFeatureEntitlement = (
      _featureKey: string,
    ): Entitlement => {
      throw new Error(
        'usePatientFeatureEntitlements should only be used by patients. Providers should use useFeatureEntitlements instead.',
      );
    };

    const useAuthenticatedPatientPermissionEntitlement = (
      _permissionKey: string,
    ): Entitlement => {
      throw new Error(
        'usePatientPermissionEntitlements should only be used by patients. Providers should use usePermissionEntitlements instead.',
      );
    };

    // Plan precedence: Premium > Pro > Free
    // If both Pro and Premium features are enabled (unexpected during normal operation),
    // Premium takes precedence as the higher-tier plan
    let plan = UserSubscriptionType.Free;
    if (hasPremiumPlan) {
      plan = UserSubscriptionType.Premium;
    } else if (hasProPlan) {
      plan = UserSubscriptionType.Pro;
    }

    return useMemo<UserEntitlementsContextType>(
      () => ({
        plan, // Used for tracking
        isPaidUser: isPaid,
        userPlanType: getUserPlanType(plan),
        isLoading: false,
        isSuccess: true,
        isUpgradeButtonVisible: !isPaid,
        hasPaidSubscription: isPaid,
        isTrustedCustomer,
        // Expose the frontegg entitlements hooks here so app components can pivot from isPaidUser to specific entitlement checks
        useFeatureEntitlements, // use frontegg sdk
        usePermissionEntitlements, // use frontegg sdk
        usePatientFeatureEntitlements:
          useAuthenticatedPatientFeatureEntitlement, // throws
        usePatientPermissionEntitlements:
          useAuthenticatedPatientPermissionEntitlement, // throws
      }),
      [plan, isPaid, isTrustedCustomer],
    );
  };

// Helper component that calls Frontegg hooks only when rendered
// Rendered conditionally as a SIBLING (not wrapper) so it doesn't affect children mounting
export const AuthenticatedEntitlementsUpdater = ({
  onUpdate,
}: {
  onUpdate: (value: UserEntitlementsContextType) => void;
}): null => {
  const contextValue = useAuthenticatedUserEntitlementsContext();

  useEffect(() => {
    onUpdate(contextValue);
  }, [contextValue, onUpdate]);

  return null;
};
